export const COOKIE_CONSENT_VERSION = 3;
export const COOKIE_CONSENT_UPDATED_EVENT = "nm-cookie-consent-updated";
export const COOKIE_CONSENT_OPEN_EVENT = "nm-cookie-consent-open";

const STORAGE_KEY = "nm_cookie_consent";

export function isGlobalPrivacyControlEnabled() {
  if (typeof navigator === "undefined") return false;
  return navigator.globalPrivacyControl === true;
}

/** True when a GA4 measurement id is configured (controls script loading only, not preference UI). */
export function siteHasOptionalAnalytics() {
  if (typeof window === "undefined") return false;
  const id = (window.nitromuleReactData?.googleAnalyticsMeasurementId || "").trim();
  return !!id;
}

function normalizeStoredConsent(parsed) {
  if (!parsed || typeof parsed !== "object") return null;

  if (parsed.v === 2 && "analytics" in parsed) {
    const optional = !!parsed.analytics;
    return {
      functional: optional,
      performance: optional,
      targeting: optional,
      source: parsed.source || "custom",
      ts: parsed.ts || 0
    };
  }

  if (parsed.v !== COOKIE_CONSENT_VERSION) return null;

  return {
    functional: !!parsed.functional,
    performance: !!parsed.performance,
    targeting: !!parsed.targeting,
    source: parsed.source || "custom",
    ts: parsed.ts || 0
  };
}

export function readCookieConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizeStoredConsent(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeCookieConsent({ functional, performance, targeting, source = "custom" }) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        v: COOKIE_CONSENT_VERSION,
        functional: !!functional,
        performance: !!performance,
        targeting: !!targeting,
        source,
        ts: Date.now()
      })
    );
  } catch (_) {}
  dispatchCookieConsentUpdated();
}

export function hasValidCookieConsentChoice() {
  return readCookieConsent() != null;
}

function hasOptionalConsent(key) {
  const stored = readCookieConsent();
  if (stored) return !!stored[key];
  if (isGlobalPrivacyControlEnabled()) return false;
  return true;
}

/** Performance cookies (e.g. Google Analytics). */
export function hasPerformanceConsent() {
  return hasOptionalConsent("performance");
}

/** @deprecated Use hasPerformanceConsent — kept for analytics loaders. */
export function hasAnalyticsConsent() {
  return hasPerformanceConsent();
}

export function hasFunctionalConsent() {
  return hasOptionalConsent("functional");
}

export function hasTargetingConsent() {
  return hasOptionalConsent("targeting");
}

/** GPC blocks optional cookies but still requires an explicit banner dismissal. */
export function applyGlobalPrivacyControlDefaultIfNeeded() {
  return isGlobalPrivacyControlEnabled();
}

export function dispatchCookieConsentUpdated() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_UPDATED_EVENT));
}

export function openCookieConsentPreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_OPEN_EVENT));
}
